import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { 
  DigitalProduct, 
  DigitalProductResponse, 
  DigitalProductState, 
  PublicFilterDigitalProduct 
} from './types';

// Helper to get cookie (as per ActivityApi/slice.ts style)
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX || "/api",
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support (matching ActivityApi/slice.ts)
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for Bearer token
api.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const initialState: DigitalProductState = {
  products: [],
  currentProduct: null,
  total: 0,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
  },
};

// Async Thunks
export const getDigitalProducts = createAsyncThunk(
  'digitalProduct/getDigitalProducts',
  async (filters: PublicFilterDigitalProduct, { rejectWithValue }) => {
    try {
      const response = await api.get<DigitalProductResponse>('/public/digital-products', {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch digital products');
    }
  }
);

export const getMoreDigitalProducts = createAsyncThunk(
  'digitalProduct/getMoreDigitalProducts',
  async (filters: PublicFilterDigitalProduct, { rejectWithValue }) => {
    try {
      const response = await api.get<DigitalProductResponse>('/public/digital-products', {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch digital products');
    }
  }
);

export const getDigitalProductById = createAsyncThunk(
  'digitalProduct/getDigitalProductById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get<DigitalProduct>(`/public/digital-products/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch digital product details');
    }
  }
);

const digitalProductSlice = createSlice({
  name: 'digitalProduct',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // getDigitalProducts
      .addCase(getDigitalProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDigitalProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.data;
        state.total = action.payload.total;
        state.pagination.page = action.payload.page;
        state.pagination.limit = action.payload.limit;
      })
      .addCase(getDigitalProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getMoreDigitalProducts
      .addCase(getMoreDigitalProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMoreDigitalProducts.fulfilled, (state, action) => {
        state.loading = false;
        // Append new products and filter duplicates if any
        const newProducts = action.payload.data.filter(
          (newProd) => !state.products.some((existing) => existing.productID === newProd.productID)
        );
        state.products = [...state.products, ...newProducts];
        state.total = action.payload.total;
        state.pagination.page = action.payload.page;
        state.pagination.limit = action.payload.limit;
      })
      .addCase(getMoreDigitalProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getDigitalProductById
      .addCase(getDigitalProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDigitalProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(getDigitalProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentProduct, setPagination } = digitalProductSlice.actions;

export default digitalProductSlice.reducer;
